import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Bell, Menu, Home, FileText, Building2, Image as ImageIcon, HelpCircle, LogOut, ChevronDown, Phone, Download, ExternalLink } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SchoolNotificationBell from "@/components/SchoolNotificationBell";
import PushEnableBanner from "@/components/PushEnableBanner";
import InstallAppDialog, { isStandalone } from "@/components/InstallAppDialog";
import AutoInstallPrompt from "@/components/AutoInstallPrompt";
import SchoolMascotChat from "@/components/mascot/SchoolMascotChat";
import ReportProblemButton from "@/components/ReportProblemButton";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/escola", label: "Início", icon: Home, exact: true, testid: "s-nav-inicio" },
  { to: "/escola/orcamentos", label: "Orçamentos", icon: FileText, testid: "s-nav-orcamentos" },
  { to: "/escola/dados", label: "Escola", icon: Building2, testid: "s-nav-escola" },
  { to: "/escola/fotos", label: "Fotos", icon: ImageIcon, testid: "s-nav-fotos" },
  { to: "/escola/ajuda", label: "Ajuda", icon: HelpCircle, testid: "s-nav-ajuda" },
];

// Compact set used only on the mobile bottom navigation
const BOTTOM_NAV = [
  { to: "/escola", label: "Início", icon: Home, exact: true, testid: "s-bnav-inicio" },
  { to: "/escola/orcamentos", label: "Orçamentos", icon: FileText, testid: "s-bnav-orcamentos" },
  { to: "/escola/solicitar", label: "Solicitar", icon: Phone, testid: "s-bnav-solicitar" },
  { to: "/escola/fotos", label: "Fotos", icon: ImageIcon, testid: "s-bnav-fotos" },
  { to: "/escola/dados", label: "Escola", icon: Building2, testid: "s-bnav-escola" },
];

const AmcBadge = () => (
  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#0f172a] flex flex-col items-center justify-center shrink-0 shadow-sm">
    <span className="font-heading font-black text-white text-[10px] md:text-xs leading-none">AMC</span>
    <span className="font-heading font-black text-white text-[9px] md:text-[11px] leading-none mt-0.5">ESCOLAS</span>
    <div className="h-[1px] w-6 bg-red-400 mt-0.5" />
    <span className="text-[6px] md:text-[7px] text-white/80 font-bold mt-0.5 tracking-widest uppercase">
      Manutenção
    </span>
  </div>
);

const SchoolHeaderAvatar = ({ school }) => {
  const photo = school?.photo_path;
  const initials = (school?.name || "ES")
    .replace(/\u200b/g, "")
    .replace(/^APM\s+DA\s+/i, "")
    .replace(/^(EMEB|EMEF|EMEI|EE|APM)\s+/i, "")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "ES";
  if (photo) {
    const src = photo.startsWith("http") ? photo : `${API}${photo}`;
    return (
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden shrink-0 border-2 border-violet-200" data-testid="s-header-avatar">
        <img src={src} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div
      className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm"
      data-testid="s-header-avatar"
    >
      <span className="font-heading font-black text-white text-sm md:text-base leading-none">{initials}</span>
    </div>
  );
};

export default function SchoolLayout() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const standalone = isStandalone();

  useEffect(() => {
    api.get("/school/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const refreshMe = () => api.get("/school/me").then((r) => setMe(r.data));

  const MobileNavSheet = () => (
    <div className="flex flex-col bg-[#1e3a8a] text-white h-full">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <AmcBadge />
        <div>
          <p className="font-heading font-bold text-sm">Portal da Escola</p>
          <p className="text-[11px] text-white/70">{me?.school?.name || ""}</p>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.exact}
            onClick={() => setSideOpen(false)}
            data-testid={`m-${n.testid}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${
                isActive ? "bg-white text-[#1e3a8a]" : "text-white/85 hover:bg-white/10"
              }`
            }
          >
            <n.icon className="w-5 h-5" strokeWidth={2.2} />
            {n.label}
          </NavLink>
        ))}
        {!standalone && (
          <button
            type="button"
            onClick={() => { setSideOpen(false); setInstallOpen(true); }}
            data-testid="m-s-install"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/85 hover:bg-white/10"
          >
            <Download className="w-5 h-5" strokeWidth={2.2} />
            Instalar app
          </button>
        )}
        <a
          href="/?preview=1"
          target="_blank"
          rel="noreferrer"
          onClick={() => setSideOpen(false)}
          data-testid="m-s-view-landing"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/85 hover:bg-white/10"
        >
          <ExternalLink className="w-5 h-5" strokeWidth={2.2} />
          Ver página inicial
        </a>
      </nav>
      <div className="p-3 border-t border-white/10">
        <button
          onClick={logout}
          data-testid="m-s-logout"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-white/85 hover:bg-white/10"
        >
          <LogOut className="w-5 h-5" />
          Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f3ff]">
      {/* Header - matches mockup: badge + title + nav + bell + Diretor(a) */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 md:px-6 h-20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={sideOpen} onOpenChange={setSideOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="s-mobile-menu">
                  <Menu className="w-6 h-6 text-[#1e3a8a]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-[#1e3a8a] text-white border-none">
                <MobileNavSheet />
              </SheetContent>
            </Sheet>
            <SchoolHeaderAvatar school={me?.school} />
            <div className="min-w-0">
              <h1
                className="font-heading text-sm md:text-xl font-extrabold text-[#1e3a8a] leading-tight truncate uppercase"
                data-testid="s-header-school-name"
              >
                {me?.school?.name || "Carregando..."}
              </h1>
              <p className="text-[10px] md:text-xs text-slate-500 leading-tight truncate">
                {me?.school?.nickname || (me?.school?.city ? `${me.school.city}${me.school.state ? " / " + me.school.state : ""}` : "Portal da escola")}
              </p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 shrink-0">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#1e3a8a]" : "text-slate-600 hover:text-[#1e3a8a]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && (
                      <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#7c3aed] rounded-full" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {!standalone && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setInstallOpen(true)}
                data-testid="s-install-btn"
                className="hidden sm:inline-flex text-[#1e3a8a] hover:bg-violet-50"
                title="Instalar app"
              >
                <Download className="w-5 h-5" />
              </Button>
            )}
            <SchoolNotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden md:flex items-center gap-2 rounded-full border border-slate-200 hover:bg-slate-50 pl-2 pr-3 py-1"
                  data-testid="s-user-menu"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-slate-600">
                      {user?.name?.[0] || "D"}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-slate-700">Diretor(a)</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="s-view-landing">
                  <a href="/?preview=1" target="_blank" rel="noreferrer" className="cursor-pointer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ver página inicial
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700" data-testid="s-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        <div className="mb-4"><PushEnableBanner /></div>
        <div className="animate-fadeInUp">
          <Outlet context={{ me, refreshMe }} />
        </div>
      </main>

      <SchoolMascotChat />
      <ReportProblemButton />

      {/* Mobile-only bottom navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(15,23,42,0.06)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="s-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {BOTTOM_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold ${
                  isActive ? "text-[#7c3aed]" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon
                    className={`w-5 h-5 ${isActive ? "text-[#7c3aed]" : "text-slate-500"}`}
                    strokeWidth={2.2}
                  />
                  <span className="leading-none">{n.label}</span>
                  {isActive && <span className="w-6 h-0.5 bg-[#7c3aed] rounded-full -mt-0.5" />}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <footer className="bg-[#1e3a8a] text-white mt-8 pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs">
          <div>
            <p className="font-medium">
              Sistema desenvolvido por <span className="font-bold">Marcio Rafael</span>
            </p>
            <p className="text-white/70 text-[11px]">
              Todos os direitos reservados — {new Date().getFullYear()} • versão 1.0{" "}
              <a href="#" className="underline hover:text-white">
                Clique aqui
              </a>
            </p>
          </div>
          <div className="md:text-right">
            <p className="font-bold">AMC Manutenção e serviços (sistema)</p>
            <p className="text-white/70 text-[11px]">Trabalhando por escolas melhores</p>
          </div>
        </div>
      </footer>
      <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} />
      <AutoInstallPrompt role="school" />
    </div>
  );
}
